
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Bot, Settings, BarChart3, Wallet, ChevronLeft, ChevronRight, Menu, Star, BotMessageSquare, Users, Shield, BookOpen, ExternalLink, Calculator, Smartphone } from 'lucide-react';
import { Sidebar, SidebarContent, SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, useSidebar } from '@/components/ui/sidebar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// Add translation mapping for CSV Analysis and Risk Calculator
const getDisplayName = (nameKey: string, t: (key: string) => string) => {
  if (nameKey === 'csv_analysis') return 'CSV Analysis';
  if (nameKey === 'risk_calculator') return 'Risk Calculator';
  if (nameKey === 'position_mappings') return 'Position Mappings';
  return t(nameKey);
};
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useLogoPreloader } from '@/hooks/useLogoPreloader';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsActiveAffiliate } from '@/hooks/useIsActiveAffiliate';
import { useAffiliateMarketAccess } from '@/hooks/useAffiliateMarketAccess';

// Define interfaces for menu items
interface MenuItem {
  nameKey: string;
  path: string;
  icon: React.FC<any>;
  tourId?: string;
  adminOnly?: boolean;
  hidden?: boolean;
  mobileOnly?: boolean;
  affiliateOnly?: boolean;
  marketAnalysisAccess?: boolean;
  external?: boolean;
}

interface MenuGroup {
  labelKey: string;
  items: MenuItem[];
  hideLabelOnMobile?: boolean;
}

const menuGroups: MenuGroup[] = [{
  labelKey: 'overview',
  hideLabelOnMobile: true,
  items: [{
    nameKey: 'dashboard',
    path: '/',
    icon: Home
  }]
}, {
  labelKey: 'trading',
  items: [{
    nameKey: 'my_bots',
    path: '/my-bots',
    icon: Bot,
    tourId: 'menu-my-bots',
    adminOnly: true // Mark this item as admin-only
  }, {
    nameKey: 'premium_bots',
    path: '/marketplace',
    icon: Star,
    tourId: 'menu-premium'
  }, {
    nameKey: 'prop_trading_bots',
    path: '/prop-trading',
    icon: BarChart3,
    tourId: 'menu-prop-trading'
  }]
}, {
  labelKey: 'tools',
  items: [{
    nameKey: 'csv_analysis',
    path: '/user/csv-analysis',
    icon: BarChart3
  }, {
    nameKey: 'risk_calculator',
    path: '/risk-calculator',
    icon: Calculator
  }, {
    nameKey: 'mini_apps',
    path: '/mini-apps',
    icon: Smartphone
  }]
}, {
  labelKey: 'management',
  items: [{
    nameKey: 'exchange_accounts',
    path: '/exchange-accounts',
    icon: Wallet,
    tourId: 'menu-accounts'
  }, {
    nameKey: 'ai_assistant_menu',
    path: '/ai-assistant',
    icon: BotMessageSquare
  }, {
    nameKey: 'edu_hub',
    path: 'https://edu.tradebot365.com/',
    icon: BookOpen,
    external: true
  }, {
    nameKey: 'build_your_bot_with_ai',
    path: '/community',
    icon: BotMessageSquare
  }, {
    nameKey: 'Market Analysis',
    path: '/market-analysis',
    icon: BarChart3,
    marketAnalysisAccess: true
  }, {
    nameKey: 'admin_panel',
    path: '/admin',
    icon: Shield,
    adminOnly: true
  }, {
    nameKey: 'position_mappings',
    path: '/admin/position-mappings',
    icon: BarChart3,
    adminOnly: true
  }, {
    nameKey: 'affiliate_program',
    path: '/affiliate',
    icon: Users,
    tourId: 'menu-affiliate',
    affiliateOnly: true  // Changed: removed hidden:true, added affiliateOnly:true
  }, {
    nameKey: 'settings',
    path: '/settings',
    icon: Settings
  }]
}];

export const AppSidebar = () => {
  const location = useLocation();
  const {
    state,
    toggleSidebar
  } = useSidebar();
  const isCollapsed = state === "collapsed";
  const {
    user
  } = useAuth();
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  
  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isUserAdmin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      // Check if admin role in user metadata
      const metadataAdmin = user.user_metadata?.role === 'admin' || user.user_metadata?.role === 'superadmin';
      
      if (metadataAdmin) return true;
      
      // Also check in database
      const { data, error } = await supabase
        .from('user_profiles')
        .select('is_admin')
        .eq('user_id', user.id)
        .single();
        
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return data?.is_admin || false;
    },
    enabled: !!user?.id,
    initialData: false
  });
  
  // Check if user is an active affiliate
  const { isActiveAffiliate } = useIsActiveAffiliate();
  
  // Check if user has market analysis access
  const { hasMarketAccess } = useAffiliateMarketAccess();
  
  // Preload logos as soon as component mounts
  useLogoPreloader();

  return <>
      {isCollapsed && <Button onClick={toggleSidebar} className={cn(
          "fixed left-3 z-20 h-9 w-9 rounded-xl p-0 shadow-lg bg-gradient-to-br from-primary/90 to-primary border-0 hover:from-primary hover:to-primary/80 transition-all duration-300 hover:scale-105",
          isMobile ? "top-10 safe-area-top" : "top-3"
        )} size="icon" variant="outline">
          <Menu size={16} className="text-primary-foreground" />
        </Button>}
      
      <Sidebar className="border-r border-border/50 bg-gradient-to-b from-background via-background/95 to-muted/20 backdrop-blur-xl">
        <SidebarHeader className="border-b border-border/30 bg-gradient-to-r from-muted/20 to-background/50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className={cn(
            "mx-0 px-6 py-4 rounded-none my-0 relative transition-all duration-300"
          )}>
            <div className={cn(
              "relative transition-all duration-300",
              isMobile ? "w-16 h-16" : "w-20 h-20",
              isCollapsed && "scale-75"
            )}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur-xl animate-pulse" />
              <ImageWithFallback 
                src="/lovable-uploads/c5290104-a025-440a-87a0-1157af3806ed.png" 
                alt={t('app_name')} 
                className={cn("object-contain transition-all duration-300 relative z-10", 
                  isCollapsed ? "scale-90" : "scale-100",
                  isMobile ? "w-16 h-16" : "w-20 h-20"
                )}
                loading="eager"
                skeletonClassName="bg-muted/30 rounded-xl"
              />
            </div>
            <button 
              onClick={toggleSidebar} 
              className={cn(
                "p-2 rounded-xl hover:bg-primary/10 transition-all duration-300 absolute right-3 top-3 group hover:scale-105", 
                !isCollapsed ? "" : "hidden"
              )}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {isCollapsed ? <ChevronRight size={18} className="relative z-10" /> : <ChevronLeft size={18} className="relative z-10" />}
              </div>
            </button>
          </div>
        </SidebarHeader>
        
        <SidebarContent className="px-2 py-4">
          <ScrollArea className="h-full pr-2" orientation="vertical">
            {menuGroups.map((group, groupIndex) => 
              <SidebarGroup key={groupIndex} className="mb-6">
                <SidebarGroupLabel className={cn(
                  "text-sm font-semibold text-muted-foreground/80 mb-3 px-3 uppercase tracking-wider",
                  isMobile && group.hideLabelOnMobile && "hidden"
                )}>
                  {t(group.labelKey)}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-1">
                    {group.items
                      .filter(item => !item.hidden)
                      .filter(item => !item.adminOnly || isAdmin)
                      .filter(item => !item.affiliateOnly || isActiveAffiliate)
                      .filter(item => !item.marketAnalysisAccess || hasMarketAccess)
                      .map((item, itemIndex) => {
                        const isActive = location.pathname === item.path;
                        const displayName = getDisplayName(item.nameKey, t);
                        
                        return (
                          <SidebarMenuItem key={item.path} className="group/item">
                            <SidebarMenuButton 
                              asChild 
                              isActive={isActive} 
                              tooltip={isCollapsed ? displayName : undefined} 
                              data-tour={item.tourId}
                              className={cn(
                                "relative rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]",
                                item.external && "bg-gradient-to-r from-orange-500/10 via-orange-400/10 to-yellow-400/10 border border-orange-400/20 hover:border-orange-400/40 hover:shadow-lg hover:shadow-orange-400/20",
                                !item.external && isActive 
                                  ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent border border-primary/20 shadow-md shadow-primary/10" 
                                  : !item.external && "hover:bg-gradient-to-r hover:from-muted/50 hover:to-transparent hover:border-border/30"
                              )}
                            >
                              {item.external ? (
                                <a 
                                  href={item.path} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 w-full"
                                >
                                  <div className={cn(
                                    "p-2 rounded-lg transition-all duration-300 group-hover/item:scale-110",
                                    item.external 
                                      ? "bg-gradient-to-br from-orange-400/20 to-yellow-400/20 text-orange-600 dark:text-orange-400 shadow-sm" 
                                      : isActive 
                                        ? "bg-primary/20 text-primary shadow-sm" 
                                        : "text-muted-foreground group-hover/item:bg-muted/30 group-hover/item:text-foreground"
                                  )}>
                                    <item.icon size={18} />
                                  </div>
                                  <span className={cn(
                                    "font-medium transition-all duration-300 flex items-center gap-2",
                                    item.external 
                                      ? "text-orange-700 dark:text-orange-300 group-hover/item:text-orange-800 dark:group-hover/item:text-orange-200" 
                                      : isActive 
                                        ? "text-foreground" 
                                        : "text-muted-foreground group-hover/item:text-foreground"
                                  )}>
                                    {displayName}
                                    {item.external && <ExternalLink size={14} className="opacity-60" />}
                                  </span>
                                  {item.external && (
                                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-full animate-pulse shadow-sm" />
                                  )}
                                </a>
                              ) : (
                                <Link to={item.path} className="flex items-center gap-3 w-full">
                                  <div className={cn(
                                    "p-2 rounded-lg transition-all duration-300 group-hover/item:scale-110",
                                    isActive 
                                      ? "bg-primary/20 text-primary shadow-sm" 
                                      : "text-muted-foreground group-hover/item:bg-muted/30 group-hover/item:text-foreground"
                                  )}>
                                    <item.icon size={18} />
                                  </div>
                                  <span className={cn(
                                    "font-medium transition-all duration-300",
                                    isActive ? "text-foreground" : "text-muted-foreground group-hover/item:text-foreground"
                                  )}>
                                    {displayName}
                                  </span>
                                  {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary to-primary/50 rounded-r-full shadow-lg shadow-primary/30" />
                                  )}
                                </Link>
                              )}
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </ScrollArea>
        </SidebarContent>

        <SidebarFooter className="border-t border-border/30 bg-gradient-to-r from-muted/10 to-background/50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className={cn("p-4", isCollapsed ? "flex justify-center" : "space-y-4")}>
            {!isCollapsed && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-muted/30 to-background/50 border border-border/30 hover:border-border/50 transition-all duration-300 hover:shadow-md group">
                <div className="relative">
                  <Avatar className="ring-2 ring-primary/20 transition-all duration-300 group-hover:ring-primary/40">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
                      {user?.user_metadata?.name?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 border-2 border-background rounded-full animate-pulse" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-foreground">
                    {user?.user_metadata?.name || t('user')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>
    </>;
};
